import { PhoenixMetrics } from "@amzn/ads-phoenix-type-script-client";
import { Button, Text, Flex, Code } from "@amzn/storm-ui";
import type { ReactElement } from "react";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { useStore } from "zustand";

import {
    TELEMETRY_ID,
    PHOENIX_FEATURE,
    PHOENIX_TELEMETRY_ID,
} from "../../../constants/telemetry";
import useEncodedUnifiedFilterStateUrl from "../../../hooks/misc/useEncodedUnifiedFilterStateUrl";
import { unifiedFilterStore } from "../../../store/unifiedFilters";
import {
    getUnifiedFilterTelemetryDetails,
    logWithTakt,
} from "../../../utils/telemetry.utils";
import { SEARCH_BAR_ID } from "../filters/SearchBar";

const StyledWrapper = styled.div`
    margin-left: auto;
`;

const HelpText = styled(Text)`
    font-size: 9px;
    color: ${({ theme }) => theme.color.gray[500]};
`;

export const ApplyFiltersButton = (): ReactElement => {
    const [setEncodedUnifiedFilterStateUrl] = useEncodedUnifiedFilterStateUrl();

    const [
        filters,
        searchQuery,
        isContentDirty,
        isMetadataDirty,
        setIsContentDirty,
        isLastSearchParametersDifferent,
    ] = useStore(unifiedFilterStore, (state) => [
        state.filters,
        state.searchQuery,
        state.isContentDirty,
        state.isMetadataDirty,
        state.setIsContentDirty,
        state.isLastSearchParametersDifferent,
    ]);

    const search = (fromEnterKey: boolean) => {
        setIsContentDirty(true);
        setEncodedUnifiedFilterStateUrl();

        if (fromEnterKey) {
            logWithTakt(TELEMETRY_ID.SEARCHED_FOR_CONTENTS, {
                searchAction: "Enter",
                ...getUnifiedFilterTelemetryDetails(),
            });
            PhoenixMetrics.emit(
                PHOENIX_TELEMETRY_ID.SEARCHED_FOR_CONTENTS_ENTER,
                PHOENIX_FEATURE.FILTERS,
                {
                    customData: {
                        filters: getUnifiedFilterTelemetryDetails(),
                    },
                },
            );
        } else {
            logWithTakt(TELEMETRY_ID.SEARCHED_FOR_CONTENTS, {
                searchAction: "Button",
                ...getUnifiedFilterTelemetryDetails(),
            });
            PhoenixMetrics.emit(
                PHOENIX_TELEMETRY_ID.SEARCHED_FOR_CONTENTS_BUTTON,
                PHOENIX_FEATURE.FILTERS,
                {
                    customData: {
                        filters: getUnifiedFilterTelemetryDetails(),
                    },
                },
            );
        }
    };

    // Track if the filters have changed since our last search
    const [localIsDirty, setLocalIsDirty] = useState(false);
    useEffect(() => {
        setLocalIsDirty(isLastSearchParametersDifferent());
    }, [filters, searchQuery]);

    /**
     * Add in a document listener for the enter key that triggers a search
     */
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            // Check if the pressed key is Enter
            if (event.key === "Enter") {
                // Check if the active element is not an input, textarea, or other form element
                const activeElement = document.activeElement;
                const isFormElement =
                    activeElement instanceof HTMLElement &&
                    (activeElement.tagName === "INPUT" ||
                        activeElement.tagName === "TEXTAREA" ||
                        activeElement.tagName === "SELECT" ||
                        activeElement.isContentEditable);

                const isActiveElementSearchBar =
                    activeElement?.id === SEARCH_BAR_ID;

                // Only proceed if no form element is focused and the search button is enabled
                // Also proceed if there is a form element focused, but it's the search bar
                if (
                    (isActiveElementSearchBar || !isFormElement) &&
                    !isContentDirty &&
                    localIsDirty
                ) {
                    search(true);
                }
            }
        };

        // Add event listener on component load
        document.addEventListener("keydown", handleKeyPress);

        // Remove our event listener when this component unloads
        return () => {
            document.removeEventListener("keydown", handleKeyPress);
        };
    }, [search, isContentDirty, localIsDirty]);

    return (
        <StyledWrapper data-testid="apply-filters-wrapper">
            <Flex direction="column" rowGap="xxxsmall">
                <Button
                    data-testid="apply-filters-button"
                    disabled={!localIsDirty}
                    variant="primary"
                    onClick={() => search(false)}
                    loading={isContentDirty || isMetadataDirty}
                    type="submit"
                >
                    Apply Filters & Search
                </Button>
                <HelpText>
                    <Code renderAs="code">↵ Enter</Code> to Apply Filters &
                    Search
                </HelpText>
            </Flex>
        </StyledWrapper>
    );
};
